import api from '../../../services/api';
import {
  normalizeOrderTransports,
  type OrderTransportsDto,
} from '../../../dto/transport';

/**
 * Fetch every transport linked to an order plus the per-product realization
 * breakdown. A single order can be carried on N transports (e.g. a 2-container
 * order delivered on 3 trucks); the response surfaces that fan-out.
 *
 * Transport-level mutations (delete, create, update) live in
 * `src/modules/transports/api/transportRoutesApi.ts` — transports are not
 * owned by an order in the data model, even when surfaced from the order page.
 */
export async function fetchOrderTransports(orderId: string): Promise<OrderTransportsDto> {
  const response = await api.get(`/orders/${orderId}/transports`);
  return normalizeOrderTransports(response.data);
}

/**
 * Unlink an order from a transport without deleting the transport itself.
 * Transports are N:N, so removing this order leaves the others linked. If the
 * unlinked order was the transport's last one, the backend cascades the
 * delete and returns `{ deleted: true }` so the UI can react (e.g. redirect
 * away from a detail page that just disappeared).
 */
export async function unlinkOrderFromTransport(
  transportId: string,
  orderId: string
): Promise<{ deleted: boolean }> {
  const response = await api.delete(`/transport-routes/${transportId}/orders/${orderId}`);
  return { deleted: Boolean(response.data?.deleted) };
}
